import * as yup from "yup";
import { i18_ARTIST } from "../../../i18nReferences";

// The states GET /artists/:id/private-mail reports for the personal address.
export const PRIVATE_MAIL_STATE_CONFIRMED = "CONFIRMED";
export const PRIVATE_MAIL_STATE_PENDING = "PENDING";
export const PRIVATE_MAIL_STATE_EXPIRED = "EXPIRED";

// The reasons the backend gives with a 403 when the login password is missing or wrong.
const PASSWORD_REQUIRED_REASON = "PASSWORD_REQUIRED";
const PASSWORD_INVALID_REASON = "PASSWORD_INVALID";

/** Days an emailed confirmation link stays valid; the dialog tells the user. */
export const PRIVATE_MAIL_LINK_VALID_DAYS = 3;

/**
 * Who may see and change the personal address of an artist: admins for every artist and each artist
 * for themselves, guests (`CannotEdit`) included. This is deliberately not `canEdit` of
 * `useArtistBase`, which excludes guests and is used by other masks.
 */
export const canEditPrivateMail = ({ isAdmin, loggedInArtistId, artistId }) =>
    Boolean(isAdmin) || (loggedInArtistId != null && artistId != null && Number(loggedInArtistId) === Number(artistId));

/** Guests are artists without admin rights who may not edit; they do not see the sender address. */
export const isGuestArtist = ({ isAdmin, cannotEdit }) => !isAdmin && Boolean(cannotEdit);

/** Artists without admin rights have to confirm a change of the personal address with their login password. */
export const isPrivateMailPasswordRequired = (isAdmin) => !isAdmin;

/**
 * Parses a UTC timestamp the backend sends as text (`YYYY-MM-DD HH:MM:SS`, no zone). This is a real
 * instant and not a studio-local appointment time, so it may become a Date; the zone has to be
 * named explicitly, otherwise the browser would read the text as its local time.
 */
export const parseUtcTimestamp = (text) => {
    if (!text) {
        return null;
    }
    const date = new Date(`${String(text).trim().replace(" ", "T")}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** The password problem of a failed change or removal, or null when the error is something else. */
export const getPrivateMailPasswordProblem = (error) => {
    const reason = error?.response?.status === 403 ? error.response.data?.reason : null;
    return reason === PASSWORD_REQUIRED_REASON || reason === PASSWORD_INVALID_REASON ? reason : null;
};

export const getPrivateMailPasswordErrorText = (problem, t) =>
    problem === PASSWORD_REQUIRED_REASON
        ? t("EDIT_ARTIST.PRIVATE_MAIL_PASSWORD_REQUIRED", {
              ns: i18_ARTIST,
              defaultValue: "Bitte das Passwort eingeben.",
          })
        : t("EDIT_ARTIST.PRIVATE_MAIL_PASSWORD_INVALID", {
              ns: i18_ARTIST,
              defaultValue: "Das Passwort ist nicht korrekt.",
          });

/** A readable text for a failed action on the personal address that is not a password problem. */
export const getPrivateMailErrorText = (error, t) => {
    if (error?.response?.status === 403) {
        return t("EDIT_ARTIST.PRIVATE_MAIL_ERROR_FORBIDDEN", {
            ns: i18_ARTIST,
            defaultValue: "Dafür fehlt die Berechtigung.",
        });
    }
    return t("EDIT_ARTIST.PRIVATE_MAIL_ERROR_GENERIC", {
        ns: i18_ARTIST,
        defaultValue: "Das hat nicht geklappt: {{message}}",
        message: error?.response?.data?.message || error?.message || "",
    });
};

export const createPrivateMailSchema = ({ isPasswordRequired }) =>
    yup.object().shape({
        Adresse: yup.string().trim().email({ key: "YUP.ERROR.KUNDE_EMAIL_FORMAT" }).max(100).required(),
        Passwort: isPasswordRequired ? yup.string().required() : yup.string().notRequired(),
    });

const getRequestText = (privateMail, t, formatDate) => {
    if (privateMail.State === PRIVATE_MAIL_STATE_EXPIRED) {
        return t("EDIT_ARTIST.PRIVATE_MAIL_STATE_EXPIRED", { ns: i18_ARTIST, defaultValue: "Link abgelaufen" });
    }
    const requestedAt = parseUtcTimestamp(privateMail.NeueAdresseAm);
    return requestedAt
        ? t("EDIT_ARTIST.PRIVATE_MAIL_STATE_PENDING_SINCE", {
              ns: i18_ARTIST,
              defaultValue: "Wartet auf Bestätigung seit {{date}}",
              date: formatDate(requestedAt),
          })
        : t("EDIT_ARTIST.PRIVATE_MAIL_STATE_PENDING", { ns: i18_ARTIST, defaultValue: "Wartet auf Bestätigung" });
};

const getConfirmedText = (t) =>
    t("EDIT_ARTIST.PRIVATE_MAIL_STATE_CONFIRMED", { ns: i18_ARTIST, defaultValue: "Bestätigt" });

/** Whether a confirmation link is outstanding (valid or expired), so it can be resent or cancelled. */
export const hasPrivateMailRequest = (privateMail) =>
    [PRIVATE_MAIL_STATE_PENDING, PRIVATE_MAIL_STATE_EXPIRED].includes(privateMail?.State);

/**
 * The addresses to show with their state, the open request first. An artist with a confirmed address
 * and an open request for a new one gets both entries.
 *
 * @param {Object} privateMail result of GET /artists/:id/private-mail
 * @param {Function} t translation function
 * @param {Function} formatDate formats a Date as a date in the studio's locale and time zone
 * @returns {Array<{kind: string, address: string, text: string, hasProblem: boolean}>} `hasProblem` marks an
 *     expired link, which the section shows in the warning color
 */
export const getPrivateMailEntries = (privateMail, t, formatDate) => {
    const entries = [];
    if (hasPrivateMailRequest(privateMail) && privateMail.NeueAdresse) {
        entries.push({
            kind: "request",
            address: privateMail.NeueAdresse,
            text: getRequestText(privateMail, t, formatDate),
            hasProblem: privateMail.State === PRIVATE_MAIL_STATE_EXPIRED,
        });
    }
    if (privateMail?.Adresse) {
        entries.push({
            kind: "confirmed",
            address: privateMail.Adresse,
            text: getConfirmedText(t),
            hasProblem: false,
        });
    }
    return entries;
};

const isSameAddress = (left, right) => (left ?? "").trim().toLowerCase() === (right ?? "").trim().toLowerCase();

/**
 * The lines that tell where login mails and notices go, only for mails that do not go to the confirmed
 * personal address: that address is already shown as "Bestätigt" above. One line when both go to the same
 * address.
 *
 * @returns {Array<{key: string, text: string}>}
 */
export const getPrivateMailRecipientLines = (privateMail, t) => {
    const confirmed = privateMail?.Adresse;
    const otherThanConfirmed = (address) => (confirmed && isSameAddress(address, confirmed) ? null : address);
    const login = otherThanConfirmed(privateMail?.LoginAdresse);
    const notice = otherThanConfirmed(privateMail?.HinweisAdresse);
    if (login && notice && isSameAddress(login, notice)) {
        return [
            {
                key: "both",
                text: t("EDIT_ARTIST.PRIVATE_MAIL_RECIPIENT_BOTH", {
                    ns: i18_ARTIST,
                    defaultValue: "Anmelde-Mails und Hinweise gehen an: {{address}}",
                    address: login,
                }),
            },
        ];
    }
    const lines = [];
    if (login) {
        lines.push({
            key: "login",
            text: t("EDIT_ARTIST.PRIVATE_MAIL_RECIPIENT_LOGIN", {
                ns: i18_ARTIST,
                defaultValue: "Anmelde-Mails gehen an: {{address}}",
                address: login,
            }),
        });
    }
    if (notice) {
        lines.push({
            key: "notice",
            text: t("EDIT_ARTIST.PRIVATE_MAIL_RECIPIENT_NOTICE", {
                ns: i18_ARTIST,
                defaultValue: "Hinweise gehen an: {{address}}",
                address: notice,
            }),
        });
    }
    return lines;
};

/**
 * The text of the personal-address column in the artist list, e.g. "lisa@gmx.de · Bestätigt" or
 * "neu@x.de · Wartet seit 14.10.2026". With a confirmed address and an open request the request is shown,
 * because it is what the admin has to watch. Empty when the artist has no entry.
 */
export const getPrivateMailColumnText = (privateMail, t, formatDate) => {
    if (!privateMail) {
        return "";
    }
    if (hasPrivateMailRequest(privateMail) && privateMail.NeueAdresse) {
        if (privateMail.State === PRIVATE_MAIL_STATE_EXPIRED) {
            return `${privateMail.NeueAdresse} · ${getRequestText(privateMail, t, formatDate)}`;
        }
        const requestedAt = parseUtcTimestamp(privateMail.NeueAdresseAm);
        const since = requestedAt
            ? t("EDIT_ARTIST.PRIVATE_MAIL_COLUMN_PENDING_SINCE", {
                  ns: i18_ARTIST,
                  defaultValue: "Wartet seit {{date}}",
                  date: formatDate(requestedAt),
              })
            : t("EDIT_ARTIST.PRIVATE_MAIL_COLUMN_PENDING", { ns: i18_ARTIST, defaultValue: "Wartet" });
        return `${privateMail.NeueAdresse} · ${since}`;
    }
    return privateMail.Adresse ? `${privateMail.Adresse} · ${getConfirmedText(t)}` : "";
};
